import { LocaleSelect } from "@app/shared/components/Locale/LocaleSelect";
import { useCurrentUserLocale } from "@app/shared/utils/currentUserLocale";
import "@app/shared/utils/locales";
import {
    getFlagByLocale,
    keys,
    RESOURCE_NAME,
    SupportedLocales,
} from "@app/shared/utils/locales";
import { Icon } from "@design-system/Icon";
import { Button } from "@design-system/Inputs/Button";
import { Text } from "@design-system/Typography/Text";
import { cn } from "@design-system/Utilities";
import { useAtom } from "jotai";
import { type FC, type PropsWithChildren } from "react";
import { useTranslation } from "react-i18next";
import { questionnaireLocaleAtom } from "../../ctx";
import { useQuestionnaireLocale } from "../../data";
import { Chip } from "@design-system/Inputs/Chip";
import { DetailChip } from "@design-system/DataDisplay/DetailChip";

export const QuestionnaireLocaleDropdown: FC<
    PropsWithChildren<{
        questionnaireId?: string;
        variant?: "light" | "dark";
        isChip?: boolean;
    }>
> = ({ questionnaireId, children, variant = "light", isChip = false }) => {
    const { t } = useTranslation(RESOURCE_NAME);
    const { availableLocales } = useQuestionnaireLocale(questionnaireId);

    const [currentLocale, setCurrentLocale] = useAtom(questionnaireLocaleAtom);
    const userLocale = useCurrentUserLocale(SupportedLocales.EN);

    const isOnlyOneLocale = availableLocales.length === 1;

    if (children && isOnlyOneLocale) {
        return (
            <Text
                className="flex items-center gap-2 p-2"
                color={variant === "dark" ? "white" : "primary"}
                variant="body-sm-bold"
            >
                <Icon
                    name={getFlagByLocale(currentLocale ?? userLocale)}
                    size="sm"
                />
                {t(keys[currentLocale ?? userLocale])}
            </Text>
        );
    }

    if (isChip && isOnlyOneLocale) {
        return (
            <DetailChip>
                <DetailChip.Icon
                    name={getFlagByLocale(currentLocale ?? userLocale)}
                />
                <DetailChip.Label>
                    {t(keys[currentLocale ?? userLocale])}
                </DetailChip.Label>
            </DetailChip>
        );
    }
    return (
        <LocaleSelect
            value={currentLocale ?? userLocale}
            onChange={setCurrentLocale}
            options={availableLocales}
        >
            {children ??
                (isChip ? (
                    <Chip size="sm">
                        <Chip.Icon
                            name={getFlagByLocale(currentLocale ?? userLocale)}
                        />
                        <Chip.Value>
                            {t(keys[currentLocale ?? userLocale])}
                        </Chip.Value>
                        <Chip.Icon name="chevronDown" />
                    </Chip>
                ) : (
                    <Button
                        className={cn(
                            "w-fit",
                            variant === "dark" &&
                                "text-white hover:bg-opacity-30 active:bg-opacity-30",
                        )}
                        variant="text"
                        size="sm"
                    >
                        <Button.Icon
                            className="mr-2"
                            name={getFlagByLocale(currentLocale ?? userLocale)}
                        />
                        {t(keys[currentLocale ?? userLocale])}
                        <Button.Icon name="chevronDown" />
                    </Button>
                ))}
        </LocaleSelect>
    );
};
